import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { NumericInput } from "./ui/numeric-input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { WorkforceReadinessPanel } from "./WorkforceReadinessPanel";
import { OrganisationStructureTree } from "./OrganisationStructureTree";
import { Users, ShieldCheck, MapPin, FileText, ClipboardCheck, Network } from "lucide-react";
import { toast } from "sonner";

const SUB_TABS = [
  { key: "structure", label: "Organisation Structure", icon: Network },
  { key: "readiness", label: "Readiness Queue", icon: Users },
  { key: "review", label: "Compliance Review", icon: ClipboardCheck },
  { key: "assign", label: "Campaign Assignment", icon: ShieldCheck },
  { key: "agreements", label: "Agreements", icon: FileText },
  { key: "venues", label: "Venue Compliance", icon: MapPin },
];

export function WorkforceOperationsCentre() {
  const [sub, setSub] = useState("structure");
  return (
    <div data-testid="workforce-operations-centre">
      <div className="flex flex-wrap gap-2 mb-5">
        {SUB_TABS.map(s => (
          <button key={s.key} onClick={() => setSub(s.key)}
            data-testid={`wfops-tab-${s.key}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              sub === s.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
            }`}>
            <s.icon className="inline h-3.5 w-3.5 mr-1" /> {s.label}
          </button>
        ))}
      </div>

      {sub === "structure" && <OrganisationStructureTree canMutate={true} />}
      {sub === "readiness" && <WorkforceReadinessPanel />}
      {sub === "review" && <ComplianceReviewPanel />}
      {sub === "assign" && <CampaignAssignmentPanel />}
      {sub === "agreements" && <AgreementsPanel />}
      {sub === "venues" && <VenueCompliancePanel />}
    </div>
  );
}

// ─────────────────────── COMPLIANCE REVIEW ───────────────────────
function ComplianceReviewPanel() {
  const [fundraisers, setFundraisers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get(`/users`);
        setFundraisers((r.data || []).filter(u => u.role === "fundraiser"));
      } catch (e) { /* */ }
    })();
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!selectedId) {
        if (active) setReadiness(null);
        return;
      }
      try {
        const r = await apiClient.get(`/workforce/users/${selectedId}/passport`);
        if (active) setReadiness(r.data);
      } catch (e) { if (active) setReadiness(null); }
    })();
    return () => { active = false; };
  }, [selectedId]);

  const approve = async () => {
    if (!selectedId) return;
    try {
      await apiClient.put(`/team/${selectedId}/approved`, { approved: true });
      toast.success("Fundraiser approved");
    } catch (e) { toast.error("Couldn't approve the submission. Please try again."); }
  };
  const reject = async () => {
    if (!selectedId) return;
    try {
      await apiClient.put(`/team/${selectedId}/approved`, { approved: false });
      toast.success("Fundraiser approval revoked");
    } catch (e) { toast.error("Couldn't reject the submission. Please try again."); }
  };

  return (
    <Card data-testid="compliance-review-panel"><CardContent className="p-5 space-y-4">
      <h3 className="font-bold">Compliance Review</h3>
      <Select value={selectedId} onValueChange={setSelectedId}>
        <SelectTrigger data-testid="cr-fundraiser-select">
          <SelectValue placeholder="Select a fundraiser…" />
        </SelectTrigger>
        <SelectContent>
          {fundraisers.map(f => (
            <SelectItem key={f.id} value={f.id}>{f.name} · {f.email}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {readiness && (
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs text-slate-500 uppercase">Passport</div>
              <div className="text-3xl font-bold">{readiness.passportScore}%</div>
            </div>
            <Badge>{readiness.passportLabel}</Badge>
          </div>
          {(readiness.passportActions || []).length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-slate-700 uppercase mb-1">
                Outstanding actions
              </div>
              <ul className="space-y-1 text-sm">
                {readiness.passportActions.map((a, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{a.severity}</Badge>
                    <span>{a.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button onClick={approve} disabled={!selectedId}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="cr-approve-btn">Approve</Button>
        <Button onClick={reject} disabled={!selectedId} variant="outline"
          data-testid="cr-reject-btn">Revoke approval</Button>
      </div>
    </CardContent></Card>
  );
}

// ─────────────────────── CAMPAIGN ASSIGNMENT ───────────────────────
function CampaignAssignmentPanel() {
  const [fundraisers, setFundraisers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [userId, setUserId] = useState("");
  const [campId, setCampId] = useState("");
  const [decision, setDecision] = useState(null);
  const [reqs, setReqs] = useState({
    dbsRequired: true, dbsMaxAgeMonths: 36, rtwRequired: true,
    selfEmployedAgreementRequired: true, trainingRequired: false,
    minAge: 18, maxAge: 75, venueNotificationRequired: false,
  });

  useEffect(() => {
    (async () => {
      const r1 = await apiClient.get(`/users`);
      setFundraisers((r1.data || []).filter(u => u.role === "fundraiser"));
      const r2 = await apiClient.get(`/campaigns`);
      setCampaigns(r2.data || []);
    })();
  }, []);

  useEffect(() => {
    if (!campId) return;
    (async () => {
      try {
        const r = await apiClient.get(`/workforce/campaigns/${campId}/compliance-requirements`);
        if (r.data) setReqs(prev => ({ ...prev, ...r.data }));
      } catch (e) { /* */ }
    })();
  }, [campId]);

  const saveReqs = async () => {
    if (!campId) return;
    try {
      await apiClient.put(`/workforce/campaigns/${campId}/compliance-requirements`, reqs);
      toast.success("Compliance requirements saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const runAssign = async () => {
    if (!userId || !campId) return;
    try {
      const r = await apiClient.post(`/workforce/assign`,
                                       { userId, campaignId: campId });
      setDecision(r.data);
      toast.success(`Decision: ${r.data.decision}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  return (
    <div className="space-y-4" data-testid="campaign-assignment-panel">
      <Card><CardContent className="p-5 space-y-4">
        <h3 className="font-bold">Campaign compliance requirements</h3>
        <Select value={campId} onValueChange={setCampId}>
          <SelectTrigger data-testid="ca-camp-select">
            <SelectValue placeholder="Select campaign…" />
          </SelectTrigger>
          <SelectContent>
            {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {campId && (
          <div className="grid grid-cols-2 gap-3">
            {[["dbsRequired", "DBS required"], ["rtwRequired", "RTW required"],
              ["selfEmployedAgreementRequired", "Self-employed agreement"],
              ["trainingRequired", "Training required"],
              ["venueNotificationRequired", "Venue notification"]].map(([k, lbl]) => (
              <div key={k} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                <Label className="text-sm">{lbl}</Label>
                <Switch checked={!!reqs[k]} onCheckedChange={v => setReqs(r => ({ ...r, [k]: v }))}
                  data-testid={`req-${k}`} />
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Label className="text-sm">DBS max age (months)</Label>
              <NumericInput integer value={reqs.dbsMaxAgeMonths ?? 36} fallback={36}
                onValueChange={v => setReqs(r => ({ ...r, dbsMaxAgeMonths: v }))}
                data-testid="req-dbs-age" className="w-20" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Min age</Label>
              <NumericInput integer value={reqs.minAge ?? 18} fallback={18}
                onValueChange={v => setReqs(r => ({ ...r, minAge: v }))}
                data-testid="req-min-age" className="w-20" />
              <Label className="text-sm">Max age</Label>
              <NumericInput integer value={reqs.maxAge ?? 75} fallback={75}
                onValueChange={v => setReqs(r => ({ ...r, maxAge: v }))}
                data-testid="req-max-age" className="w-20" />
            </div>
            <div className="col-span-2">
              <Button onClick={saveReqs} className="bg-slate-900 hover:bg-slate-800"
                data-testid="req-save-btn">Save requirements</Button>
            </div>
          </div>
        )}
      </CardContent></Card>

      <Card><CardContent className="p-5 space-y-4">
        <h3 className="font-bold">Run assignment decision</h3>
        <div className="flex gap-2 flex-wrap">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-64" data-testid="ca-fr-select">
              <SelectValue placeholder="Fundraiser…" />
            </SelectTrigger>
            <SelectContent>
              {fundraisers.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={campId} onValueChange={setCampId}>
            <SelectTrigger className="w-64" data-testid="ca-camp2-select">
              <SelectValue placeholder="Campaign…" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={runAssign} disabled={!userId || !campId}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="ca-assign-btn">Run decision</Button>
        </div>
        {decision && (
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200"
            data-testid="ca-decision-card">
            <div className="font-semibold mb-1">Decision: {decision.decision}</div>
            <div className="text-xs text-slate-600">
              Score: {decision.readinessScore} · Missing:{" "}
              {(decision.missingItems || []).join(", ") || "none"}
            </div>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}

// ─────────────────────── AGREEMENTS ───────────────────────
function AgreementsPanel() {
  const [fundraisers, setFundraisers] = useState([]);
  const [userId, setUserId] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await apiClient.get(`/users`);
      setFundraisers((r.data || []).filter(u => u.role === "fundraiser"));
    })();
  }, []);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const r = await apiClient.get(`/workforce/users/${userId}/agreements`);
        setHistory(r.data || []);
      } catch (e) { setHistory([]); }
    })();
  }, [userId]);

  const signOnBehalf = async () => {
    try {
      const target = fundraisers.find(f => f.id === userId);
      await apiClient.post(`/workforce/agreements/sign`,
        { userId, version: "v1.0", signedName: target?.name || userId,
          documentReference: "agreement-v1.0.pdf" });
      toast.success("Agreement signed");
      const r = await apiClient.get(`/workforce/users/${userId}/agreements`);
      setHistory(r.data || []);
    } catch (e) { toast.error("Sign failed"); }
  };

  const revoke = async (id) => {
    try {
      await apiClient.delete(`/workforce/agreements/${id}?reason=manager_revoke`);
      toast.success("Agreement revoked");
      const r = await apiClient.get(`/workforce/users/${userId}/agreements`);
      setHistory(r.data || []);
    } catch (e) { toast.error("Couldn't revoke the record. Please try again."); }
  };

  return (
    <Card data-testid="agreements-panel"><CardContent className="p-5 space-y-4">
      <h3 className="font-bold">Agreements</h3>
      <Select value={userId} onValueChange={setUserId}>
        <SelectTrigger data-testid="ag-fr-select">
          <SelectValue placeholder="Fundraiser…" />
        </SelectTrigger>
        <SelectContent>
          {fundraisers.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {userId && (
        <>
          <Button onClick={signOnBehalf} className="bg-slate-900 hover:bg-slate-800"
            data-testid="ag-sign-btn">Sign agreement on behalf</Button>
          <div className="space-y-2">
            {history.length === 0 && <div className="text-sm text-slate-500">No agreements yet.</div>}
            {history.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                data-testid={`agreement-row-${a.id}`}>
                <div>
                  <div className="font-medium text-sm">{a.version} · {a.status}</div>
                  <div className="text-xs text-slate-500">
                    {a.signedAt ? `Signed ${String(a.signedAt).slice(0, 10)}` : ""}
                    {a.revokedAt ? ` · Revoked ${String(a.revokedAt).slice(0, 10)}` : ""}
                  </div>
                </div>
                {a.status === "signed" && (
                  <Button size="sm" variant="outline" onClick={() => revoke(a.id)}
                    data-testid={`ag-revoke-${a.id}`}>Revoke</Button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </CardContent></Card>
  );
}

// ─────────────────────── VENUE COMPLIANCE ───────────────────────
function VenueCompliancePanel() {
  const [locations, setLocations] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ locationId: "", venue: "", postcode: "", councilArea: "" });
  const [refreshTick, setRefreshTick] = useState(0);
  const [permitInput, setPermitInput] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rL = await apiClient.get(`/locations`);
        if (alive) setLocations(rL.data || []);
        const rV = await apiClient.get(`/workforce/venue-compliance`);
        if (alive) setRecords(rV.data || []);
      } catch (e) { /* */ }
    })();
    return () => { alive = false; };
  }, [refreshTick]);

  const bump = () => setRefreshTick(t => t + 1);

  const callAction = async (payload, successMsg) => {
    try {
      await apiClient.post(`/workforce/venue-compliance`, payload);
      toast.success(successMsg);
      bump();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const notify = () => {
    if (!form.locationId) return;
    return callAction({
      action: "create_notification",
      locationId: form.locationId, venue: form.venue,
      postcode: form.postcode, councilArea: form.councilArea,
      notificationReference: `NOTIF-${Date.now()}`,
    }, "Venue notification created");
  };

  const ack = (locId) => callAction({
    action: "acknowledge", locationId: locId,
    acknowledgementReference: `ACK-${Date.now()}`,
  }, "Acknowledged");

  const permit = async (locId) => {
    const ref = permitInput[locId];
    if (!ref) {
      toast.error("Enter a permit reference first");
      return;
    }
    try {
      await apiClient.post(`/workforce/venue-compliance`, {
        action: "upload_permit", locationId: locId,
        permitReference: ref, permitExpiresAt: "2027-12-31",
      });
      toast.success("Permit uploaded");
      setRefreshTick(t => t + 1);
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <Card data-testid="venue-compliance-panel"><CardContent className="p-5 space-y-4">
      <h3 className="font-bold">Venue Compliance</h3>
      <div className="grid grid-cols-2 gap-2">
        <Select value={form.locationId} onValueChange={v => setForm(f => ({ ...f, locationId: v }))}>
          <SelectTrigger data-testid="vc-loc-select"><SelectValue placeholder="Location…" /></SelectTrigger>
          <SelectContent>
            {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Venue name" value={form.venue}
          onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
          data-testid="vc-venue" />
        <Input placeholder="Postcode" value={form.postcode}
          onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))}
          data-testid="vc-postcode" />
        <Input placeholder="Council area" value={form.councilArea}
          onChange={e => setForm(f => ({ ...f, councilArea: e.target.value }))}
          data-testid="vc-council" />
      </div>
      <Button onClick={notify} className="bg-slate-900 hover:bg-slate-800"
        data-testid="vc-notify-btn">Create notification</Button>
      <div className="space-y-2">
        {records.map(r => (
          <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
            data-testid={`venue-row-${r.locationId}`}>
            <div>
              <div className="font-medium text-sm">{r.venue || r.locationId} · {r.status}</div>
              <div className="text-xs text-slate-500">{r.notificationReference || ""}</div>
            </div>
            <div className="flex gap-1">
              {r.status === "notified" && (
                <Button size="sm" variant="outline" onClick={() => ack(r.locationId)}
                  data-testid={`vc-ack-${r.locationId}`}>Acknowledge</Button>
              )}
              {(r.status === "acknowledged" || r.status === "notified") && (
                <>
                  <Input
                    placeholder="Permit ref"
                    value={permitInput[r.locationId] || ""}
                    onChange={e => setPermitInput(p => ({ ...p, [r.locationId]: e.target.value }))}
                    className="w-32 h-8 text-xs"
                    data-testid={`vc-permit-ref-${r.locationId}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => permit(r.locationId)}
                    data-testid={`vc-permit-${r.locationId}`}>Add permit</Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}
